import React, { Component } from "react";
import { Box, Button, CircularProgress } from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardHeader from "components/Card/CardHeader.jsx";

import { organizationReportService } from "_services/organizationReport.service";
import { ReportBody, PrintReportButton, injectTailwindCdn } from "views/organizationReport/index";
import { USER_INFO } from "__helpers/constants";

// Org-wide Executive Dashboard for the Org Admin: the Organization Report engine over
// EVERY facility under the org (resolved server-side). Includes the multi-facility
// network table ("facilities") plus KPIs/funnel/activity/backlog/speed/alerts. Excludes
// the demo-only hero/$-ROI/MaxAlly blocks (hardcoded sample numbers).
const ORG_SECTIONS = ["kpis", "funnel", "facilities", "activity", "backlog", "speed", "alerts", "maxally"];

function getOrgName() {
  try {
    const info = JSON.parse(localStorage.getItem(USER_INFO)) || {};
    if (info.context && info.context.orgAdmin && info.context.orgAdmin.orgName) {
      return info.context.orgAdmin.orgName;
    }
  } catch (e) {
    /* ignore */
  }
  return "My Organization";
}

export default class OrgExecutiveDashboard extends Component {
  constructor(props) {
    super(props);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    thirtyDaysAgo.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.orgName = getOrgName();
    this.state = {
      startDate: thirtyDaysAgo,
      endDate: today,
      loading: false,
      data: null,
      error: null,
      hasGenerated: false,
    };
  }

  componentDidMount() {
    injectTailwindCdn();
    // Auto-fire the default last-30-days report on landing (org admin lands here at login).
    this.handleGenerate();
  }

  handleGenerate = async () => {
    const { startDate, endDate } = this.state;
    if (!startDate || !endDate) return;
    this.setState({ loading: true, error: null, hasGenerated: true });
    try {
      const data = await organizationReportService.fetchOrgReport({ startDate, endDate });
      this.setState({ data, loading: false });
    } catch (e) {
      this.setState({ error: e.message, loading: false, data: null });
    }
  };

  render() {
    const { startDate, endDate, loading, data, hasGenerated, error } = this.state;

    const kpis = (data && data.networkKpis) || {};
    const funnel = (data && data.funnel) || {};
    const activity = (data && data.activityByDay) || [];
    const backlog = (data && data.canAcceptBacklog) || [];
    const facilities = (data && data.facilities) || [];
    const speed = (data && data.speedDistribution) || {};
    const alerts = (data && data.criticalAlerts) || [];
    const maxAlly = (data && data.maxAlly) || {};
    const noActivity = hasGenerated && data && !kpis.totalSends && !kpis.uniqueReferrals;

    return (
      <div className="orgreport-root">
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              <CardHeader
                color="success"
                className={"cusCardheader cusCardTitle"}
              >
                <h4>Executive Dashboard</h4>
              </CardHeader>
              <CardBody>
                <Box display="flex" flexWrap="wrap" gridGap={16} alignItems="center" mb={2}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      label="Start Date"
                      format="MM/dd/yyyy"
                      value={startDate}
                      onChange={(d) => this.setState({ startDate: d })}
                      maxDate={endDate || new Date()}
                    />
                    <KeyboardDatePicker
                      label="End Date"
                      format="MM/dd/yyyy"
                      value={endDate}
                      onChange={(d) => this.setState({ endDate: d })}
                      minDate={startDate}
                      maxDate={new Date()}
                    />
                  </MuiPickersUtilsProvider>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.handleGenerate}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={20} /> : "Generate Report"}
                  </Button>
                  {/* Lives in the toolbar, not in the report body — the body scrolls. */}
                  {data && !loading ? <PrintReportButton /> : null}
                </Box>

                {error ? (
                  <Box mb={2} style={{ color: "#dc2626", fontSize: "0.85rem" }}>
                    {error}
                  </Box>
                ) : null}

                {!hasGenerated ? (
                  <Box py={6} textAlign="center" style={{ color: "#6b6969", fontSize: "0.9rem" }}>
                    Select a date range, then click <strong>Generate Report</strong> to view your
                    organization's report.
                  </Box>
                ) : loading ? (
                  /* Wait for the API before rendering any report elements — avoids the flash of
                     undefined/zero values while the request is in flight. */
                  <Box
                    py={8}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    style={{ color: "#6b6969" }}
                  >
                    <CircularProgress />
                    <Box mt={2} style={{ fontSize: "0.9rem" }}>
                      Generating report…
                    </Box>
                  </Box>
                ) : data ? (
                  <div
                    className="orgreport-body"
                    style={{
                      maxHeight: "78vh",
                      overflowY: "auto",
                      overflowX: "hidden",
                      // Breathing room so the last section card isn't flush against the scroll
                      // edge, and so its content clears the scrollbar.
                      paddingBottom: 16,
                      paddingRight: 4,
                    }}
                  >
                    {noActivity ? (
                      <Box mb={2} style={{ color: "#7b7979", fontSize: "0.85rem" }}>
                        No referral activity across <strong>{this.orgName}</strong> in the selected
                        date range.
                      </Box>
                    ) : null}
                    <ReportBody
                      organization={{ name: this.orgName }}
                      window={{ startDate, endDate }}
                      kpis={kpis}
                      funnel={funnel}
                      activity={activity}
                      backlog={backlog}
                      facilities={facilities}
                      speed={speed}
                      alerts={alerts}
                      maxAlly={maxAlly}
                      sections={ORG_SECTIONS}
                      footer={false}
                    />
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}
